import { Color, Spacing } from '@persona-id/design-tokens';
import { CircleAlertIcon, MoreIcon, PlusIcon } from '@persona-id/design-tokens/icons';
import { XIcon } from '@persona-id/design-tokens/icons/12px';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { JSONSchema7Object } from 'json-schema';
import { DateTime } from 'luxon';
import { ComponentProps, CSSProperties, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';

import { LoadingShimmerParagraph } from 'dashboard/components/LoadingShimmer/LoadingShimmer';
import NavBack from 'dashboard/components/NavBack';
import Pagination from 'dashboard/components/Pagination';
import ProductFeatureGated from 'dashboard/components/ProductFeatureGated';
import { MonoSpan } from 'dashboard/components/Typography';
import { ArrayFieldTemplate } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/ArrayFieldTemplate';
import DateTimeWidget from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/DateTimeWidget';
import DateWidget from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/DateWidget';
import { FieldTemplate } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/FieldTemplate';
import { HandlebarsField } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/HandlebarsField';
import { InputWidget } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/InputWidget';
import { ObjectFieldTemplate } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/ObjectFieldTemplate';
import { PDSCheckBoxField } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/PDSCheckBoxField';
import { SelectWidget } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/SelectWidget';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import TableLayout, {
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import useTableState from 'dashboard/lib/hooks/useTableState';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { titleCase } from 'dashboard/lib/utils/stringFormat';
import {
  ExternalIntegrationConfig,
  ExternalIntegrationConfigCredentialApiKeyPayload,
  ExternalIntegrationConfigCredentialBasicPayload,
  ExternalIntegrationConfigCredentialBearerPayload,
  ExternalIntegrationConfigCredentialOauth2PasswordPayload,
  ExternalIntegrationConfigPayload,
} from 'dashboard/models/external-integration-config';
import {
  ExternalIntegrationVendor,
  ExternalIntegrationVendorSecurityScheme,
  ExternalIntegrationVendorServer,
  ExternalIntegrationVendorServerVariables,
} from 'dashboard/models/external-integration-vendor';
import { ProductFeatureName } from 'dashboard/models/organization';
import { useListExternalIntegrationLogsQuery } from 'dashboard/redux/api/external-integration-log';
import { useTestExternalIntegrationVendorMutation } from 'dashboard/redux/api/external-integration-vendor';
import Badge from 'design-system/components/Badge';
import Banner from 'design-system/components/Banner';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card/Card';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import { SelectField, TextField } from 'design-system/components/FormField';
import Modal from 'design-system/components/Modal/Modal';
import { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Heading, Paragraph } from 'design-system/components/Text';
import { headingMixins } from 'design-system/mixins/text';
import IconButton from 'inquiry/components/IconButton';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import { useRTKResultHandler } from 'lib/hooks/useRTKResultHandler';
import { ApiModel } from 'lib/interfaces/api';
import { useFormSecurely } from 'lib/security/reactHookForm';

const DropdownSecuritySchemeButton = ({
  schemes,
  selectSecurityScheme,
  style,
}: {
  schemes: ExternalIntegrationVendorSecurityScheme[];
  selectSecurityScheme: (scheme: ExternalIntegrationVendorSecurityScheme) => void;
  style: CSSProperties;
}) => (
  <DropdownMenu
    trigger={
      <Button
        style={style}
        id='integrations-show__add-credential'
        width='auto'
        prefixIcon={<PlusIcon />}
      >
        Add Credential
      </Button>
    }
  >
    {schemes.map((scheme) => (
      <DropdownMenu.Item
        key={scheme['security-scheme-name']}
        onSelect={() => selectSecurityScheme(scheme)}
      >
        {scheme.description}
      </DropdownMenu.Item>
    ))}
  </DropdownMenu>
);

const LoadingRow = () => (
  <TableRow>
    <TableCell>
      <LoadingShimmerParagraph width='50%' />
    </TableCell>
    <TableCell>
      <LoadingShimmerParagraph width='50%' />
    </TableCell>
    <TableCell>
      <LoadingShimmerParagraph width='50%' />
    </TableCell>
    <TableCell>
      <LoadingShimmerParagraph width='50%' />
    </TableCell>
    <TableCell>
      <Button width='icon' variant='inverse' disabled>
        <MoreIcon />
      </Button>
    </TableCell>
  </TableRow>
);

const LoadingRowLog = () => (
  <TableRow>
    <TableCell>
      <LoadingShimmerParagraph width='50%' />
    </TableCell>
    <TableCell>
      <LoadingShimmerParagraph width='50%' />
    </TableCell>
    <TableCell>
      <LoadingShimmerParagraph width='50%' />
    </TableCell>
    <TableCell>
      <LoadingShimmerParagraph width='50%' />
    </TableCell>
    <TableCell>
      <LoadingShimmerParagraph width='50%' />
    </TableCell>
  </TableRow>
);

const ServerSelectField = ({
  servers,
  selectedServer,
  variables,
  selectedVariables,
  onServerChange,
  onVariablesChange,
}: {
  servers: ExternalIntegrationVendorServer[];
  selectedServer: string;
  variables: { [key: string]: ExternalIntegrationVendorServerVariables };
  selectedVariables: { [key: string]: string };
  onServerChange: (value: string) => void;
  onVariablesChange: (value: { [key: string]: string }) => void;
}) => {
  const handleServerChange = (url: string) => {
    onServerChange(url);
  };

  useEffect(() => {
    if (!selectedServer && servers.length > 0) {
      onServerChange(servers[0].url);
    }
  }, [selectedServer, servers, onServerChange]);

  return (
    <>
      {servers.length > 1 && (
        <SelectField
          label='Server'
          placeholder='Select a Server'
          required
          onValueChange={handleServerChange}
          value={selectedServer}
        >
          {servers.map((config) => (
            <Option key={config.url} value={config.url}>
              {config.description || config.url}
            </Option>
          ))}
        </SelectField>
      )}

      {variables &&
        Object.keys(variables).length > 0 &&
        Object.entries(variables).map(([key, variable]) => (
          <TextField
            key={key}
            label={variable.description || key}
            value={selectedVariables[key] || ''}
            onChange={(e) =>
              onVariablesChange({
                ...selectedVariables,
                [key]: e.target.value,
              })
            }
            required
          />
        ))}
    </>
  );
};

const BearerSchemeForm = ({
  onSubmit,
  securitySchemeName,
  vendorName,
  servers,
  uiSchema,
}: {
  onSubmit: (data: ExternalIntegrationConfigPayload) => void;
  securitySchemeName: string;
  vendorName: string;
  servers: ExternalIntegrationVendorServer[];
  uiSchema: ExternalIntegrationVendorSecurityScheme['ui-schema'];
}) => {
  const [selectedServer, setSelectedServer] = useState<string>(servers[0].url);

  const [selectedVariables, setSelectedVariables] = useState<Record<string, string>>(
    Object.fromEntries(
      Object.entries(servers[0]?.variables || {}).map(([key, value]) => [key, value.default || '']),
    ),
  );

  const { register, handleSubmit } = useFormSecurely<
    ExternalIntegrationConfigCredentialBearerPayload['attributes']
  >({
    mode: 'onBlur',
  });

  const handleServerChange = (url: string) => {
    setSelectedServer(url);

    const newServer = servers.find((s) => s.url === url);

    setSelectedVariables(
      Object.fromEntries(
        Object.entries(newServer?.variables || {}).map(([key, value]) => [
          key,
          value.default || '',
        ]),
      ),
    );
  };

  const submitApiKeyConfig = (
    data: ExternalIntegrationConfigCredentialBearerPayload['attributes'],
  ) => {
    let updatedUrl = selectedServer;
    Object.entries(selectedVariables).forEach(([key, value]) => {
      updatedUrl = updatedUrl.replace(`{${key}}`, value || key);
    });

    onSubmit({
      type: ApiModel.ExternalIntegrationConfigCredentialBearer,
      attributes: {
        ...data,
        server: updatedUrl,
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(submitApiKeyConfig)}>
      <ServerSelectField
        servers={servers}
        selectedServer={selectedServer}
        variables={servers.find((s) => s.url === selectedServer)?.variables || {}}
        selectedVariables={selectedVariables}
        onServerChange={handleServerChange}
        onVariablesChange={setSelectedVariables}
      />

      <TextField
        {...register('label')}
        label='Nickname for Credential'
        placeholder='Production Credential'
        required
      />
      <TextField
        {...register('bearer-token')}
        label={uiSchema?.['api-key']?.['ui:title'] || 'API Key'}
        type='password'
        autoComplete='off'
        required
      />
      <input {...register('security-scheme-name')} value={securitySchemeName} hidden />
      <input {...register('vendor-name')} value={vendorName} hidden />
      <Button type='submit'>Create</Button>
    </form>
  );
};

const ApiKeyEntryForm = ({
  onSubmit,
  securitySchemeName,
  vendorName,
  keyName,
  keyLocation,
  servers,
  uiSchema,
}: {
  onSubmit: (data: ExternalIntegrationConfigPayload) => void;
  securitySchemeName: string;
  vendorName: string;
  keyName: string;
  keyLocation: string;
  servers: ExternalIntegrationVendorServer[];
  uiSchema: ExternalIntegrationVendorSecurityScheme['ui-schema'];
}) => {
  const [selectedServer, setSelectedServer] = useState<string>(servers[0].url);

  const [selectedVariables, setSelectedVariables] = useState<Record<string, string>>(
    Object.fromEntries(
      Object.entries(servers[0]?.variables || {}).map(([key, value]) => [key, value.default || '']),
    ),
  );

  const { register, handleSubmit } = useFormSecurely<
    ExternalIntegrationConfigCredentialApiKeyPayload['attributes']
  >({
    mode: 'onBlur',
  });

  const handleServerChange = (url: string) => {
    setSelectedServer(url);

    const newServer = servers.find((s) => s.url === url);

    setSelectedVariables(
      Object.fromEntries(
        Object.entries(newServer?.variables || {}).map(([key, value]) => [
          key,
          value.default || '',
        ]),
      ),
    );
  };

  const submitApiKeyConfig = (
    data: ExternalIntegrationConfigCredentialApiKeyPayload['attributes'],
  ) => {
    let updatedUrl = selectedServer;
    Object.entries(selectedVariables).forEach(([key, value]) => {
      updatedUrl = updatedUrl.replace(`{${key}}`, value || key);
    });

    onSubmit({
      type: ApiModel.ExternalIntegrationConfigCredentialApiKey,
      attributes: {
        ...data,
        server: updatedUrl,
      },
    });
  };
  return (
    <form onSubmit={handleSubmit(submitApiKeyConfig)}>
      <ServerSelectField
        servers={servers}
        selectedServer={selectedServer}
        variables={servers.find((s) => s.url === selectedServer)?.variables || {}}
        selectedVariables={selectedVariables}
        onServerChange={handleServerChange}
        onVariablesChange={setSelectedVariables}
      />

      <TextField
        {...register('label')}
        label='Nickname for Credential'
        placeholder='Production Credential'
        required
      />
      <TextField
        {...register('key')}
        label={uiSchema?.['api-key']?.['ui:title'] || 'API Key'}
        type='password'
        autoComplete='off'
        required
      />
      <input {...register('security-scheme-name')} value={securitySchemeName} hidden />
      <input {...register('vendor-name')} value={vendorName} hidden />
      <input {...register('key-name')} value={keyName} hidden />
      <input {...register('location')} value={keyLocation} hidden />
      <Button type='submit'>Create</Button>
    </form>
  );
};

const BasicAuthEntryForm = ({
  onSubmit,
  securitySchemeName,
  vendorName,
  servers,
  uiSchema,
}: {
  onSubmit: (data: ExternalIntegrationConfigPayload) => void;
  securitySchemeName: string;
  vendorName: string;
  servers: ExternalIntegrationVendorServer[];
  uiSchema: ExternalIntegrationVendorSecurityScheme['ui-schema'];
}) => {
  const [selectedServer, setSelectedServer] = useState<string>(servers[0].url);

  const [selectedVariables, setSelectedVariables] = useState<Record<string, string>>(
    Object.fromEntries(
      Object.entries(servers[0]?.variables || {}).map(([key, value]) => [key, value.default || '']),
    ),
  );

  const { register, handleSubmit } = useFormSecurely<
    ExternalIntegrationConfigCredentialBasicPayload['attributes']
  >({
    mode: 'onBlur',
  });

  const handleServerChange = (url: string) => {
    setSelectedServer(url);

    const newServer = servers.find((s) => s.url === url);

    setSelectedVariables(
      Object.fromEntries(
        Object.entries(newServer?.variables || {}).map(([key, value]) => [
          key,
          value.default || '',
        ]),
      ),
    );
  };

  const submitBasicAuthConfig = (
    data: ExternalIntegrationConfigCredentialBasicPayload['attributes'],
  ) => {
    let updatedUrl = selectedServer;
    Object.entries(selectedVariables).forEach(([key, value]) => {
      updatedUrl = updatedUrl.replace(`{${key}}`, value || key);
    });

    onSubmit({
      type: ApiModel.ExternalIntegrationConfigCredentialBasic,
      attributes: {
        ...data,
        server: updatedUrl,
      },
    });
  };
  return (
    <form onSubmit={handleSubmit(submitBasicAuthConfig)}>
      <ServerSelectField
        servers={servers}
        selectedServer={selectedServer}
        variables={servers.find((s) => s.url === selectedServer)?.variables || {}}
        selectedVariables={selectedVariables}
        onServerChange={handleServerChange}
        onVariablesChange={setSelectedVariables}
      />

      <TextField
        {...register('label')}
        label='Nickname for Credential'
        placeholder='Production Credential'
        required
      />
      <TextField
        {...register('username')}
        label={uiSchema?.username?.['ui:title'] || 'Username'}
        type='text'
        autoComplete='off'
        required
      />
      <TextField
        {...register('password')}
        label={uiSchema?.password?.['ui:title'] || 'Password'}
        type='password'
        autoComplete='off'
        required
      />
      <input {...register('security-scheme-name')} value={securitySchemeName} hidden />
      <input {...register('vendor-name')} value={vendorName} hidden />
      <Button type='submit'>Create</Button>
    </form>
  );
};

const Oauth2PasswordEntryForm = ({
  onSubmit,
  securitySchemeName,
  vendorName,
  servers,
  uiSchema,
}: {
  onSubmit: (data: ExternalIntegrationConfigPayload) => void;
  securitySchemeName: string;
  vendorName: string;
  servers: ExternalIntegrationVendorServer[];
  uiSchema: ExternalIntegrationVendorSecurityScheme['ui-schema'];
}) => {
  const [selectedServer, setSelectedServer] = useState<string>(servers[0].url);

  const [selectedVariables, setSelectedVariables] = useState<Record<string, string>>(
    Object.fromEntries(
      Object.entries(servers[0]?.variables || {}).map(([key, value]) => [key, value.default || '']),
    ),
  );

  const { register, handleSubmit } = useFormSecurely<
    ExternalIntegrationConfigCredentialOauth2PasswordPayload['attributes']
  >({
    mode: 'onBlur',
  });

  const handleServerChange = (url: string) => {
    setSelectedServer(url);

    const newServer = servers.find((s) => s.url === url);

    setSelectedVariables(
      Object.fromEntries(
        Object.entries(newServer?.variables || {}).map(([key, value]) => [
          key,
          value.default || '',
        ]),
      ),
    );
  };

  const submitBasicAuthConfig = (
    data: ExternalIntegrationConfigCredentialOauth2PasswordPayload['attributes'],
  ) => {
    let updatedUrl = selectedServer;
    Object.entries(selectedVariables).forEach(([key, value]) => {
      updatedUrl = updatedUrl.replace(`{${key}}`, value || key);
    });

    onSubmit({
      type: ApiModel.ExternalIntegrationConfigCredentialOauth2Password,
      attributes: {
        ...data,
        server: updatedUrl,
      },
    });
  };
  return (
    <form onSubmit={handleSubmit(submitBasicAuthConfig)}>
      <ServerSelectField
        servers={servers}
        selectedServer={selectedServer}
        variables={servers.find((s) => s.url === selectedServer)?.variables || {}}
        selectedVariables={selectedVariables}
        onServerChange={handleServerChange}
        onVariablesChange={setSelectedVariables}
      />

      <TextField
        {...register('label')}
        label='Nickname for Credential'
        placeholder='Production Credential'
        required
      />
      <TextField
        {...register('username')}
        label={uiSchema?.username?.['ui:title'] || 'Username'}
        type='text'
        autoComplete='off'
        required
      />
      <TextField
        {...register('password')}
        label={uiSchema?.password?.['ui:title'] || 'Password'}
        type='password'
        autoComplete='off'
        required
      />
      <input {...register('security-scheme-name')} value={securitySchemeName} hidden />
      <input {...register('vendor-name')} value={vendorName} hidden />
      <Button type='submit'>Create</Button>
    </form>
  );
};

const ControlPanelContainer = styled.div`
  background-color: ${({ theme }) => theme.color.background.primary};
  width: 400px;
  flex: none;
  padding: ${Spacing[3]};
  border-left: 1px solid ${Color.BorderHairline};
  overflow: visible;
`;

const VendorContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  overflow: hidden;
`;

const PanelContainer = styled.div`
  background-color: ${({ theme }) => theme.color.background.primary};
  padding: ${Spacing[3]};
  overflow: visible;
`;

const SubTitle = styled.div`
  ${headingMixins.sm}
`;

const DashboardIntegrationsShowView = ({
  vendor,
  onCreateConfig,
  onDeleteConfig,
  onArchiveConfig,
  vendorConfigs,
  isCreatingConfig,
  isFetchingConfigs,
}: {
  vendor: ExternalIntegrationVendor;
  onCreateConfig: (payload: ExternalIntegrationConfigPayload) => void;
  onDeleteConfig: (configId: string) => void;
  onArchiveConfig: (config: ExternalIntegrationConfig) => void;
  vendorConfigs: ExternalIntegrationConfig[];
  isCreatingConfig: boolean;
  isFetchingConfigs: boolean;
}) => {
  const [securityScheme, selectSecurityScheme] =
    useState<ExternalIntegrationVendorSecurityScheme>();
  const [configToDelete, selectConfigToDelete] = useState<ExternalIntegrationConfig>();
  const handleFormSubmit = (data: ExternalIntegrationConfigPayload) => {
    selectSecurityScheme(undefined);
    onCreateConfig(data);
  };

  const [
    testExternalIntegrationVendor,
    { isLoading: isCreatingLog, data: testVendorData, error: testVendorError },
  ] = useTestExternalIntegrationVendorMutation();

  const [showTestedPanel, setShowTestedPanel] = useState<boolean>(false);
  const [operation, setOperation] = useState<string>(
    vendor['operation-schema'] ? Object.keys(vendor['operation-schema'])[0] : '',
  );
  const [requestedData, setRequestData] = useState<JSONSchema7Object | null>(null);
  const [requestedParameters, setRequestParameters] = useState<JSONSchema7Object>({});
  const [credentialId, setCredentialId] = useState<string>('');
  const [credentialLabel, setCredentialLabel] = useState<string>('');

  const updateOperation = (value: string) => setOperation(value);

  const {
    tableState: { currentPage, pageSize, setPageSize, setCurrentPage },
  } = useTableState({});

  const { data: { data: logs = [], meta: paginationParams } = {}, isFetching } =
    useListExternalIntegrationLogsQuery({
      filter: {
        'vendor-id': vendor.vendor,
      },
      page: {
        number: currentPage,
        size: pageSize,
      },
    });

  useRTKErrorHandler({
    error: testVendorError,
    onError: (error) => ({
      message: (error?.errors?.at(0)?.details as string) || error.message,
      level: MessageType.Failure,
    }),
  });

  useRTKResultHandler({
    data: testVendorData,
    onResult: () => ({
      message: `Integration Test for ${
        vendor['operation-schema'][
          testVendorData?.data?.attributes?.integration?.['operation-id'] || operation
        ].title
      } is Complete`,
      level: MessageType.Success,
    }),
  });

  let securitySchemeForm: JSX.Element;
  if (securityScheme?.type === 'http' && securityScheme.scheme === 'basic') {
    securitySchemeForm = (
      <BasicAuthEntryForm
        onSubmit={handleFormSubmit}
        securitySchemeName={securityScheme['security-scheme-name'] || 'undefined'}
        vendorName={vendor.vendor}
        servers={vendor.servers}
        uiSchema={securityScheme['ui-schema'] || {}}
      />
    );
  } else if (securityScheme?.type === 'apiKey') {
    securitySchemeForm = (
      <ApiKeyEntryForm
        onSubmit={handleFormSubmit}
        securitySchemeName={securityScheme['security-scheme-name'] || 'undefined'}
        vendorName={vendor.vendor}
        keyName={securityScheme['key-name'] || 'undefined'}
        keyLocation={securityScheme['location'] || 'undefined'}
        servers={vendor.servers}
        uiSchema={securityScheme['ui-schema'] || {}}
      />
    );
  } else if (securityScheme?.type === 'oauth2' && 'password' in (securityScheme?.flows || {})) {
    securitySchemeForm = (
      <Oauth2PasswordEntryForm
        onSubmit={handleFormSubmit}
        securitySchemeName={securityScheme['security-scheme-name'] || 'undefined'}
        vendorName={vendor.vendor}
        servers={vendor.servers}
        uiSchema={securityScheme['ui-schema'] || {}}
      />
    );
  } else {
    securitySchemeForm = (
      <BearerSchemeForm
        onSubmit={handleFormSubmit}
        securitySchemeName={securityScheme?.['security-scheme-name'] || 'undefined'}
        vendorName={vendor.vendor}
        servers={vendor.servers}
        uiSchema={securityScheme?.['ui-schema'] || {}}
      />
    );
  }

  const formProps: Partial<ComponentProps<typeof Form>> = {
    fields: {
      BooleanField: PDSCheckBoxField,
      handlebars: HandlebarsField,
    },
    widgets: {
      TextWidget: InputWidget,
      SelectWidget,
      DateWidget,
      DateTimeWidget,
    },
    templates: {
      FieldTemplate,
      ObjectFieldTemplate,
      ArrayFieldTemplate,
    },
    showErrorList: false,
  };

  return (
    <>
      <Helmet title={`Integrations - ${vendor.title}`} />
      <Container>
        <Header
          title={`Integration / ${vendor.title}`}
          level='detail'
          prepend={<NavBack to='/dashboard/integrations' aria-label='Back to integrations' />}
        />
        <VendorContainer aria-label='Platform integration vendor' role='application'>
          <Container>
            <Section>
              <ProductFeatureGated invert productFeatureFlag={ProductFeatureName.WorkflowsEnhanced}>
                <Banner variant='alert' css={{ marginBottom: '20px', padding: '8px' }}>
                  <CircleAlertIcon />
                  <Paragraph size='lg'>
                    You do not have access to this feature.{' '}
                    <a href='https://withpersona.com/contact'>Contact us</a> if you are interested.
                  </Paragraph>
                </Banner>
              </ProductFeatureGated>
              <Card>
                <Card.Body>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '16px',
                      paddingBottom: '16px',
                    }}
                  >
                    {vendor['icon-path'] && (
                      <div
                        style={{
                          display: 'flex',
                          width: '40px',
                          minWidth: '40px',
                          height: '40px',
                          backgroundColor: '#F4F4F6',
                          borderRadius: '4px',
                          padding: '4px',
                        }}
                      >
                        <img
                          src={vendor['icon-path']}
                          style={{ width: '100%', height: 'auto' }}
                          alt={`${vendor.title} logo`}
                        />
                      </div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'start',
                        justifyContent: 'center',
                        width: 'auto',
                        flexGrow: '1',
                      }}
                    >
                      <Heading size='sm'>{vendor.title}</Heading>
                      <Paragraph>{vendor.description}</Paragraph>
                    </div>
                    <ProductFeatureGated productFeatureFlag={ProductFeatureName.WorkflowsEnhanced}>
                      {vendor['security-schemes'].length > 1 ? (
                        <DropdownSecuritySchemeButton
                          style={{ alignSelf: 'start' }}
                          schemes={vendor['security-schemes']}
                          selectSecurityScheme={(scheme) => selectSecurityScheme(scheme)}
                        />
                      ) : (
                        <Button
                          id='integrations-show__add-credential'
                          style={{ alignSelf: 'start' }}
                          width='auto'
                          prefixIcon={<PlusIcon />}
                          onClick={() => selectSecurityScheme(vendor['security-schemes'][0])}
                        >
                          Add Credential
                        </Button>
                      )}
                    </ProductFeatureGated>
                  </div>
                  <Card.SectionHeader>Credentials</Card.SectionHeader>
                  <div css={{ overflowX: 'auto' }}>
                    <Table isLoading={isFetchingConfigs}>
                      <TableHead sticky>
                        <TableRow>
                          <TableHeaderCell>Name</TableHeaderCell>
                          <TableHeaderCell>Created by</TableHeaderCell>
                          <TableHeaderCell>Status</TableHeaderCell>
                          <TableHeaderCell>Created at</TableHeaderCell>
                          <TableHeaderCell width='min' />
                          <TableHeaderCell width='min' />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {vendorConfigs.map((config) => (
                          <TableRow key={`integrations-show__config-row__${config.id}`}>
                            <TableCell>{config.attributes.label}</TableCell>
                            <TableCell>{config.attributes.creator}</TableCell>
                            <TableCell>
                              <Sticker
                                variant={config.attributes.status === 'active' ? 'green' : 'grey'}
                              >
                                {titleCase(config.attributes.status)}
                              </Sticker>
                            </TableCell>
                            <TableCell>
                              {DateTime.fromISO(config.attributes['created-at']).toFormat(
                                DATE_AND_TIME_FORMAT,
                              )}
                            </TableCell>
                            <TableCell>
                              <Button
                                id={`integrations-show__config-row__${config.id}__test`}
                                variant='primary'
                                aria-label={`test button for ${config.attributes.label}`}
                                onClick={() => {
                                  setCredentialLabel(config.attributes.label);
                                  setCredentialId(config.id);
                                  setShowTestedPanel(true);
                                }}
                              >
                                Test
                              </Button>
                            </TableCell>
                            <TableCell>
                              <DropdownMenu
                                trigger={
                                  <Button
                                    id={`integrations-show__config-row__${config.id}__dropdown`}
                                    width='icon'
                                    variant='inverse'
                                    aria-label={`more options for ${config.attributes.label}`}
                                  >
                                    <MoreIcon />
                                  </Button>
                                }
                              >
                                <DropdownMenu.Item onSelect={() => onArchiveConfig(config)}>
                                  Archive
                                </DropdownMenu.Item>
                                <DropdownMenu.Item
                                  variant='danger'
                                  onSelect={() => selectConfigToDelete(config)}
                                >
                                  Delete
                                </DropdownMenu.Item>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                        {isCreatingConfig && <LoadingRow />}
                      </TableBody>
                    </Table>
                  </div>
                  {!isCreatingConfig && vendorConfigs.length === 0 && (
                    <div
                      style={{
                        display: 'flex',
                        height: '176px',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 'auto',
                      }}
                    >
                      <Paragraph size='lg' weight='bold' style={{ color: 'black' }}>
                        Nothing to see here yet
                      </Paragraph>
                      <Paragraph size='md'>Add a credential to get started</Paragraph>
                    </div>
                  )}
                </Card.Body>
              </Card>
              <Card>
                <Card.Body>
                  <Card.SectionHeader>Integration Logs</Card.SectionHeader>
                  <TableLayout
                    isLoading={isFetching}
                    isEmpty={logs.length === 0 && !isCreatingLog}
                    headerTR={
                      <>
                        <TableTH>ID</TableTH>
                        <TableTH>Creator</TableTH>
                        <TableTH>Response Status</TableTH>
                        <TableTH>Status</TableTH>
                        <TableTH>{`Created at (${DateTime.local().toFormat(
                          TIMEZONE_NAME_ABBREVIATED,
                        )})`}</TableTH>
                      </>
                    }
                    nullState={<TableNullState>No rows found</TableNullState>}
                  >
                    {isCreatingLog && <LoadingRowLog />}
                    {logs.map((log) => (
                      <TableLinkTR
                        to={`/dashboard/integration-logs/${log.id}`}
                        key={`integration-log-table__row-${log.id}`}
                        id={`integration-log-table__row-${log.id}`}
                      >
                        <TableTD>
                          <MonoSpan>{log.id}</MonoSpan>
                        </TableTD>
                        <TableTD>
                          {log.relationships.creator.data?.id ? (
                            <MonoSpan>{log.relationships.creator.data.id}</MonoSpan>
                          ) : (
                            <Sticker>Archived</Sticker>
                          )}
                        </TableTD>
                        <TableTD>
                          <Sticker
                            variant={
                              log.attributes['response-status-code'] === '200' ||
                              log.attributes['response-status-code'] === '201'
                                ? 'green'
                                : 'red'
                            }
                          >
                            {log.attributes['response-status-code']}
                          </Sticker>
                        </TableTD>
                        <TableTD>
                          <Sticker
                            variant={log.attributes.status === 'created' ? 'green' : 'yellow'}
                          >
                            {log.attributes.status}
                          </Sticker>
                        </TableTD>
                        <TableTD>
                          {DateTime.fromISO(log.attributes['created-at']).toFormat(
                            DATE_AND_TIME_FORMAT,
                          )}
                        </TableTD>
                      </TableLinkTR>
                    ))}
                  </TableLayout>
                  <Pagination
                    isLoading={isFetching}
                    pageSize={pageSize}
                    paginationParams={paginationParams}
                    handlePageSizeClick={setPageSize}
                    handlePageLinkClick={setCurrentPage}
                  />
                </Card.Body>
              </Card>
            </Section>
            <Modal
              title={`Add new ${securityScheme?.description}`}
              open={securityScheme != null}
              onOpenChange={(open) => {
                !open && selectSecurityScheme(undefined);
              }}
            >
              <Card>{securitySchemeForm}</Card>
            </Modal>
            <Modal
              title={`Confirm deletion of '${configToDelete?.attributes.label}'`}
              open={configToDelete != null}
              onOpenChange={(open) => {
                !open && selectConfigToDelete(undefined);
              }}
            >
              <Card>
                <Spacer height={2} />
                <Paragraph>
                  This will immediately delete {configToDelete?.attributes.label} and affect any
                  integration that uses it.
                </Paragraph>
                <Spacer height={2} />
                <div style={{ display: 'flex', gap: '16px' }}>
                  <Button
                    id='integrations-show__delete-modal__cancel'
                    variant='basic'
                    onClick={() => selectConfigToDelete(undefined)}
                  >
                    Cancel
                  </Button>
                  <Button
                    id='integrations-show__delete-modal__confirm'
                    onClick={() => {
                      configToDelete && onDeleteConfig(configToDelete.id);
                      selectConfigToDelete(undefined);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </Card>
            </Modal>
          </Container>
          {showTestedPanel ? (
            <ControlPanelContainer
              aria-label='Step configuration panel'
              role='dialog'
              id='workflow-control-panel__container'
            >
              <PanelContainer>
                <div
                  css={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <IconButton
                    id='credential-testing-panel__close'
                    onClick={() => setShowTestedPanel(false)}
                    type='button'
                    aria-label='close'
                  >
                    <XIcon height={16} width={16} />
                  </IconButton>
                </div>
                <>
                  <div
                    css={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <SubTitle>Select Operation</SubTitle>
                    <Badge>{credentialLabel}</Badge>
                  </div>
                  <SelectField
                    placeholder='Select an operation'
                    onValueChange={updateOperation}
                    value={operation}
                    errorText=''
                  >
                    {Object.entries(vendor['operation-schema']).map(([key, schema]) => (
                      <Option key={key} value={key}>
                        {schema.title || key}
                      </Option>
                    ))}
                  </SelectField>
                  <Spacer height={2} />
                  <SubTitle>Configuration</SubTitle>
                  {Object.keys(
                    vendor['operation-schema'][operation].request.parametersSchema.properties || {},
                  ).length > 0 && (
                    <Form
                      id={`form-parameters-${vendor.vendor}-${operation}`}
                      idPrefix={`params-parameters-${vendor.vendor}-${operation}`}
                      idSeparator='__'
                      css={{
                        // The <Form /> element has a 16px spacer that we should offset.
                        marginBottom: -16,
                        paddingBottom: 0,
                      }}
                      formData={{}}
                      onChange={(data, _) => {
                        setRequestParameters(data.formData as JSONSchema7Object);
                      }}
                      schema={vendor['operation-schema'][operation].request.parametersSchema}
                      uiSchema={{
                        ...vendor['operation-schema'][operation].request.parametersUiSchema,
                        'ui:submitButtonOptions': { norender: true },
                      }}
                      validator={validator}
                      {...formProps}
                    />
                  )}
                  <Form
                    id={`form-${vendor.vendor}-${operation}`}
                    idPrefix={`params-${vendor.vendor}-${operation}`}
                    idSeparator='__'
                    formData={{}}
                    onChange={(data, _) => {
                      setRequestData(data.formData as JSONSchema7Object);
                    }}
                    schema={vendor['operation-schema'][operation].request.schema}
                    uiSchema={{
                      ...vendor['operation-schema'][operation].request.uiSchema,
                      'ui:submitButtonOptions': { norender: true },
                    }}
                    validator={validator}
                    {...formProps}
                  />
                </>
              </PanelContainer>
              <HStack equal>
                <Button
                  type='button'
                  id='credential-testing-panel__button-test'
                  onClick={async () => {
                    await testExternalIntegrationVendor({
                      vendorToken: vendor['vendor'],
                      requestData: requestedData,
                      requestParameters: requestedParameters,
                      operationId: operation,
                      integrationCredential: credentialId,
                    });
                  }}
                >
                  Test
                </Button>
                <Button
                  type='button'
                  id='credential-testing-panel__button-cancel'
                  onClick={() => setShowTestedPanel(false)}
                >
                  Cancel
                </Button>
              </HStack>
            </ControlPanelContainer>
          ) : null}
        </VendorContainer>
      </Container>
    </>
  );
};

export default DashboardIntegrationsShowView;
